import React, {
  useEffect,
  forwardRef,
  useState,
  useImperativeHandle
} from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import * as S from './styles'
import {
  FileWithPreview,
  getExtensionsByType,
  megabytesToBytes,
  mergeFilesAndPreviews,
  translateUploadError,
  uploadAreaStyles,
  uploadAreaStylesMinified,
  getExtensionsList
} from './helpers'
import { ReactComponent as UploadIcon } from '../../../assets/icons/arrow-upload.svg'
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete.svg'
import { ReactComponent as ReloadIcon } from '../../../assets/icons/reload.svg'
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg'

import { ReactComponent as ErrorIcon } from '../../../assets/icons/error.svg'
import { ReactComponent as ProcessingIcon } from '../../../assets/icons/processing.svg'
import { ReactComponent as ReadyIcon } from '../../../assets/icons/ready.svg'
import { ReactComponent as PlayIcon } from '../../../assets/icons/player.svg'

import { Oval } from '@agney/react-loading'
import { translation } from '../translation'

const getFileNameFromUrl = (url: string): string => {
  if (url === undefined || url === '') return 'Vídeo'

  const parts = url?.split('/')

  if (parts === undefined) return 'Vídeo'

  const fileNameWithExtension = parts[parts.length - 1]
  if (fileNameWithExtension !== undefined) {
    const fileName = fileNameWithExtension?.split('.')[0]

    return fileName
  }

  return 'Vídeo'
}

interface UploadProps {
  data: string[] | null
  setData: React.Dispatch<React.SetStateAction<string[]>>
  setNewFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>
  setDeletedFiles: React.Dispatch<React.SetStateAction<string[]>>
  props: {
    type: 'doc' | 'img' | 'video' | 'spreadsheet' | 'mix' | 'certificate'
    status?: 'pending' | 'processing' | 'error' | 'ready' | null
    maxSize: number
    maxQuantity: number
    title?: string
    description?: string
    label?: string
    minWidth?: string
    fullPreview?: boolean
    minified?: boolean
    small?: boolean
    required?: boolean
  }
}

export interface UploadMethods {
  clearFiles: () => void
}

const UploadPrimary = forwardRef<UploadMethods, UploadProps>(
  ({ data, setData, setNewFiles, setDeletedFiles, props }, ref) => {
    const strings = translation['pt']
    const [files, setFiles] = useState<FileWithPreview[]>([])
    const [style, setStyle] = useState<any>()
    const [styleMinified, setStyleMinified] = useState<any>()
    const [errorState, setError] = useState<string>()
    const [durations, setDurations] = useState<{ [key: string]: number }>({})
    const updateDuration = (previewUrl: string, duration: number) => {
      setDurations(prev => ({ ...prev, [previewUrl]: duration }))
      setNewFiles(prev =>
        prev.map(file =>
          file.preview === previewUrl ? Object.assign(file, { duration }) : file
        )
      )
      setFiles(prev =>
        prev.map(file =>
          file.preview === previewUrl ? Object.assign(file, { duration }) : file
        )
      )
    }

    const previews = mergeFilesAndPreviews(files, data).map(preview => ({
      ...preview,
      duration: durations[preview.preview] ?? preview.duration
    }))

    const {
      getRootProps,
      getInputProps,
      isFocused,
      isDragAccept,
      isDragReject
    } = useDropzone({
      accept: getExtensionsByType(props.type),
      maxFiles: props.maxQuantity,
      maxSize: megabytesToBytes(props.maxSize) || 150000000,
      validator: (file: File) => {
        if (previews.findIndex(e => e.name === file.name) > -1) {
          return {
            code: 'file-exists',
            message: ''
          }
        }
        return null
      },
      onDrop: (acceptedFiles, rejectedFiles) => {
        const totalFiles =
          files.length + acceptedFiles.length + (data?.length || 0)
        if (totalFiles > props.maxQuantity) {
          if (props.maxQuantity > 1) {
            const adjacentFiles = acceptedFiles.slice(
              -(totalFiles - props.maxQuantity)
            )
            acceptedFiles = acceptedFiles.slice(
              0,
              acceptedFiles.length - adjacentFiles.length
            )
            rejectedFiles.push(
              ...adjacentFiles.map(rejected => ({
                file: rejected,
                errors: [
                  {
                    message: '',
                    code: 'too-many-files'
                  }
                ]
              }))
            )
          } else {
            setData(() => [])
            if (acceptedFiles.length > 1) {
              acceptedFiles.splice(0, acceptedFiles.length - 1)
            }
          }
        }

        const hasToClean =
          totalFiles > props.maxQuantity && props.maxQuantity <= 1

        acceptedFiles.map((newFile: any) => {
          const obj = Object.assign(newFile, {
            preview: URL.createObjectURL(newFile)
          })
          setFiles(prev => {
            if (hasToClean) return [obj]
            return [...prev, obj]
          })
          setNewFiles(prev => {
            if (hasToClean) return [obj]
            return [...prev, obj]
          })
          setError(undefined)
        })
        rejectedFiles?.map((error: FileRejection) => {
          setError(translateUploadError(error.errors[0].code, props, 'pt'))
        })
      }
    })

    useImperativeHandle(ref, () => ({
      clearFiles() {
        setFiles([])
        setNewFiles([])
        setData(() => [])
        setDeletedFiles([])
        setDurations({})
      }
    }))

    const handleDelete = (url: string) => {
      setFiles(prev => prev.filter(e => e.preview !== url))
      setData(prev => prev.filter(e => e !== url))
      setNewFiles(prev => prev.filter(e => e.preview !== url))
      if (data && data.findIndex(e => e === url) > -1) {
        setDeletedFiles(prev => [...prev, url])
      }
      setDurations(prev => {
        const newDurations = { ...prev }
        delete newDurations[url]
        return newDurations
      })
    }

    const statusVideoLabels = {
      ready: (
        <>
          <ReadyIcon /> Tudo certo! Vídeo disponibilizado na KeedClass.
        </>
      ),
      processing: (
        <>
          <ProcessingIcon /> Estamos processando seu vídeo...
        </>
      ),
      pending: (
        <>
          <ReadyIcon /> Envio pendente...
        </>
      ),
      error: (
        <>
          <ErrorIcon /> Houve um erro, clique para tentar novamente.
        </>
      )
    }

    const ExtensionTypes = () => {
      const validTypes = [
        'doc',
        'img',
        'video',
        'spreadsheet',
        'certificate'
      ] as const
      const type = validTypes.includes(props.type as any) ? props.type : 'img'
      const extensions = getExtensionsList(type as (typeof validTypes)[number])

      return (
        <div>
          {extensions.map(extension => (
            <span key={extension}>{extension}</span>
          ))}
        </div>
      )
    }

    const ExtensionUniqueType = () => {
      return (
        <div>
          <span>{previews[previews.length - 1].extension.toUpperCase()}</span>
        </div>
      )
    }

    useEffect(() => {
      setStyle({
        ...uploadAreaStyles.base,
        ...(isFocused ? uploadAreaStyles.focused : {}),
        ...(isDragAccept ? uploadAreaStyles.accept : {}),
        ...(isDragReject ? uploadAreaStyles.rejected : {})
      })

      setStyleMinified({
        ...uploadAreaStylesMinified.base,
        ...(isFocused ? uploadAreaStylesMinified.focused : {}),
        ...(isDragAccept ? uploadAreaStylesMinified.accept : {}),
        ...(isDragReject ? uploadAreaStylesMinified.rejected : {})
      })
    }, [isFocused, isDragAccept, isDragReject])

    return (
      <S.Container
        $small={props.small}
        $video={props.type === 'video'}
        style={
          props.minWidth
            ? {
                width: props.minWidth,
                pointerEvents:
                  props.type === 'video' &&
                  (props.status === 'pending' || props.status === 'processing')
                    ? 'none'
                    : 'auto'
              }
            : {}
        }
      >
        {props.label && (
          <span>
            {props.label} {props.required && <b>*</b>}
          </span>
        )}
        <S.Wrapper
          {...getRootProps({
            style: props.minified ? styleMinified : style
          })}
        >
          <input {...getInputProps()} />

          {props.status !== undefined &&
          props.status !== null &&
          props.type === 'video' ? (
            <S.StatusContainer>
              <nav>
                <nav
                  style={{
                    color: props.status === 'error' ? 'var(--red-alert) ' : '',
                    backgroundColor:
                      props.status === 'error'
                        ? 'var(--red-alert-opacity-15)'
                        : ''
                  }}
                >
                  {statusVideoLabels[props.status]}
                </nav>
              </nav>

              <section>
                <div>
                  <div />
                </div>
                <nav>
                  <div>
                    <PlayIcon />
                  </div>
                  <p>{getFileNameFromUrl(data?.[0] || '')}</p>
                </nav>
              </section>
            </S.StatusContainer>
          ) : (
            <>
              {props.minified && (
                <S.EmptyContainerMinified ext={props.type}>
                  {previews.length > 0 ? (
                    props.maxQuantity > 1 ? (
                      previews.map((file, i) => (
                        <FilePreviewMinified
                          key={i}
                          url={file.preview}
                          name={file.name}
                          extension={file.extension}
                          type={props.type}
                        />
                      ))
                    ) : (
                      <FilePreviewMinified
                        url={previews[previews.length - 1].preview}
                        name={previews[previews.length - 1].name}
                        extension={previews[previews.length - 1].extension}
                        type={props.type}
                      />
                    )
                  ) : (
                    <div>
                      <UploadIcon />
                    </div>
                  )}
                  <nav>
                    <nav>
                      {previews.length > 0 ? (
                        <h3>{previews[previews.length - 1].name}</h3>
                      ) : (
                        <h3>{props.description}</h3>
                      )}
                      {previews.length > 0 ? (
                        <ExtensionUniqueType />
                      ) : (
                        <ExtensionTypes />
                      )}
                    </nav>
                    {previews.length > 0 && (
                      <div>
                        <button
                          onClick={e => {
                            e.stopPropagation()
                            if (typeof ref === 'function') {
                            } else if (ref && 'current' in ref && ref.current) {
                              ref.current.clearFiles()
                            }
                          }}
                        >
                          <CloseIcon />
                        </button>
                        <button>
                          <ReloadIcon />
                        </button>
                      </div>
                    )}
                  </nav>
                </S.EmptyContainerMinified>
              )}

              {!props.minified && (
                <S.Row>
                  {props.fullPreview ? (
                    previews.length === 0 ||
                    (props.type === 'video' &&
                      data !== null &&
                      data.length > 0) ? (
                      <S.EmptyContainer>
                        <div>
                          <UploadIcon />
                        </div>

                        <h1 style={{ fontSize: props.small ? 14 : 16 }}>
                          {props.title}
                        </h1>
                        <h3
                          style={{
                            fontWeight: 400,
                            fontSize: props.small ? 12 : 14
                          }}
                        >
                          {props.description}
                        </h3>
                      </S.EmptyContainer>
                    ) : (
                      <></>
                    )
                  ) : (
                    <S.EmptyContainer>
                      <div>
                        <UploadIcon />
                      </div>

                      <h1 style={{ fontSize: props.small ? 14 : 16 }}>
                        {props.title}
                      </h1>
                      <h3
                        style={{
                          fontWeight: 400,
                          fontSize: props.small ? 12 : 14
                        }}
                      >
                        {props?.description || strings.description}
                      </h3>
                    </S.EmptyContainer>
                  )}

                  {previews.length > 0 &&
                    previews !== undefined &&
                    previews !== null &&
                    previews.length > 0 &&
                    previews[0]?.name !== '' &&
                    (props.maxQuantity === 1 ? (
                      <S.PreviewsContainer
                        style={{
                          justifyContent: props.type === 'video' ? 'center' : ''
                        }}
                      >
                        <FilePreview
                          url={previews[previews.length - 1].preview}
                          name={previews[previews.length - 1].name}
                          extension={previews[previews.length - 1].extension}
                          type={props.type}
                          handleDelete={() =>
                            handleDelete(previews[previews.length - 1].preview)
                          }
                          small={props.small}
                          updateDuration={updateDuration}
                        />
                      </S.PreviewsContainer>
                    ) : (
                      <S.PreviewsContainer>
                        {previews.map((file, i) => (
                          <FilePreview
                            url={file.preview}
                            name={file.name}
                            extension={file.extension}
                            type={props.type}
                            handleDelete={() => handleDelete(file.preview)}
                            small={props.small}
                            previews={previews}
                            updateDuration={updateDuration}
                          />
                        ))}
                      </S.PreviewsContainer>
                    ))}
                </S.Row>
              )}
            </>
          )}
        </S.Wrapper>
        <nav></nav>
        {typeof errorState !== 'number' && errorState !== undefined && (
          <S.Error>
            <span>{strings.errorLabel}: </span>
            {errorState}
            <button onClick={() => setError(undefined)}>
              <DeleteIcon />
            </button>
          </S.Error>
        )}
      </S.Container>
    )
  }
)

type FilePreviewType = {
  url: string
  name: string
  extension: string
  type: 'doc' | 'img' | 'video' | 'spreadsheet' | 'mix' | 'certificate'
  handleDelete: () => void
  small?: boolean
  previews?: {
    name: string
    preview: string
    extension: string
    duration?: number
  }[]
  updateDuration?: (url: string, duration: number) => void
}

type FilePreviewTypeMinified = {
  url: string
  name: string
  extension: string
  type: 'doc' | 'img' | 'video' | 'spreadsheet' | 'mix' | 'certificate'
}

const FilePreview: React.FC<FilePreviewType> = ({
  url,
  name,
  extension,
  type,
  handleDelete,
  small,
  previews,
  updateDuration
}) => {
  const [loadedImage, setLoadedImage] = useState(false)
  const limitWidth = previews?.length && previews?.length <= 2

  const handleVideoLoadedMetadata = (
    event: React.SyntheticEvent<HTMLVideoElement>
  ) => {
    if (type === 'video' && updateDuration) {
      const duration = event.currentTarget.duration
      updateDuration(url, duration)
    }
  }

  return (
    <S.FilePreviewContainer
      style={{
        maxWidth: type !== 'mix' ? '100%' : limitWidth ? '100%' : '',
        width: limitWidth ? '100%' : '',
        maxHeight: small ? '140px' : '260px'
      }}
    >
      {type === 'video' ? (
        <video
          controls
          src={url}
          style={{
            height: 140
          }}
          onLoadedMetadata={handleVideoLoadedMetadata}
        />
      ) : type === 'img' ? (
        <img
          src={url}
          onLoad={() => {
            url && setLoadedImage(true)
          }}
        />
      ) : (
        <S.FullSizePreview ext={extension}>
          <div>{extension}</div>
          <span style={{ maxWidth: '100%' }}>{name}</span>
        </S.FullSizePreview>
      )}
      {type === 'img' && (
        <S.LoadImage loaded={loadedImage}>
          <Oval />
        </S.LoadImage>
      )}
      <S.DeleteButton
        onClick={(e: any) => {
          e.stopPropagation()
          handleDelete()
        }}
      >
        <DeleteIcon />
      </S.DeleteButton>
    </S.FilePreviewContainer>
  )
}

const FilePreviewMinified: React.FC<FilePreviewTypeMinified> = ({
  url,
  name,
  extension,
  type
}) => {
  return (
    <S.FilePreviewContainer>
      {type === 'img' ? (
        <img
          src={url}
          style={{
            objectFit: 'cover',
            borderRadius: '4px 0 0 4px',
            minWidth: 76.77,
            maxWidth: 76.77,
            padding: 4,
            height: 48
          }}
        />
      ) : (
        <S.FullSizePreview ext={extension}>
          <div>{extension}</div>
        </S.FullSizePreview>
      )}
      {/* {(type === 'img' || type === 'video') && (
        <S.LoadImage
          loaded={loadedImage}
          style={{ minWidth: 76.77, width: 76.77, borderRadius: '4px 0 0 4px' }}
        >
          <Oval />
        </S.LoadImage>
      )} */}
    </S.FilePreviewContainer>
  )
}

export default UploadPrimary
